import { Box, ButtonBase, Typography } from '@mui/material';
import { Check, User, X } from 'lucide-react';
import { useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { formatPatientName } from '~/containers/patient-list/model';
import type { QueueEntry } from '../model';
import {
    QUEUE_STATE_COLOR,
    QUEUE_STATE_LABEL_ID,
    queueRowState,
} from './importStatus';
import { entryBaseSx, selectedEntrySx } from './railStyles';

/** With onSelect the rows are controls (while reviewing); without, they are a plain record. */
export type QueueListProps = {
    entries: readonly QueueEntry[];
    /** Undefined leaves no row marked current, which is what the summary stage wants. */
    current?: number;
    onSelect?: (index: number) => void;
};

// The patient queue, nested under the active "This computer" source as an indented block.
export default function QueueNest({
    entries,
    current,
    onSelect,
}: QueueListProps) {
    const intl = useIntl();
    return (
        <Box
            role="list"
            aria-label={intl.formatMessage({
                id: 'patientList.import.queue.label',
            })}
            sx={{
                ml: 2.5,
                pl: 1.5,
                borderLeft: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                // The queue is the rail's only unbounded section, so it is the
                // one that scrolls; minHeight lets it shrink past its rows,
                // which are deliberately unsquishable.
                minHeight: 0,
                overflowY: 'auto',
                // overflow-x cannot stay visible once overflow-y is set. The
                // selected row bleeds 12px right and paints its accent 1px
                // beyond that, onto the rail divider, so the padding box has to
                // reach 13px or that last pixel counts as horizontal overflow
                // and raises a scrollbar. hidden guards against sub-pixel
                // rounding doing the same.
                mr: '-13px',
                pr: '13px',
                overflowX: 'hidden',
            }}
        >
            {entries.map(({ drop, status }, index) => {
                const isCurrent = index === current;
                const state = queueRowState(status, isCurrent);
                const label =
                    formatPatientName(drop.identity.dicomPatientName) ||
                    drop.identity.dicomPatientId;
                const rowSx = [entryBaseSx, isCurrent ? selectedEntrySx : {}];
                const content = (
                    <>
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                flexShrink: 0,
                                color: isCurrent
                                    ? 'primary.main'
                                    : 'text.disabled',
                            }}
                        >
                            <User size={14} />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                                flex: 1,
                            }}
                        >
                            <Typography
                                noWrap
                                sx={{
                                    fontSize: 12.5,
                                    color: drop.identity.dicomPatientName
                                        ? isCurrent
                                            ? 'text.primary'
                                            : 'text.secondary'
                                        : 'text.disabled',
                                }}
                            >
                                <SensitiveInfoTypography>
                                    {label}
                                </SensitiveInfoTypography>
                            </Typography>
                            <Typography
                                sx={{ fontSize: 11, color: 'text.disabled' }}
                            >
                                {intl.formatMessage(
                                    {
                                        id: 'patientList.import.queue.fileCount',
                                    },
                                    { count: drop.instanceCount }
                                )}{' '}
                                ·{' '}
                                <Box
                                    component="span"
                                    sx={{ color: QUEUE_STATE_COLOR[state] }}
                                >
                                    {intl.formatMessage({
                                        id: QUEUE_STATE_LABEL_ID[state],
                                    })}
                                </Box>
                            </Typography>
                        </Box>
                        {state === 'imported' && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    color: 'success.main',
                                }}
                            >
                                <Check size={14} />
                            </Box>
                        )}
                        {state === 'skipped' && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    color: 'warning.main',
                                }}
                            >
                                <X size={13} />
                            </Box>
                        )}
                    </>
                );
                // The listitem wraps the row, not the ButtonBase, so it can't override the button role.
                return (
                    <Box
                        key={drop.identity.dicomPatientId}
                        role="listitem"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexShrink: 0,
                        }}
                    >
                        {onSelect === undefined ? (
                            <Box sx={rowSx}>{content}</Box>
                        ) : (
                            <ButtonBase
                                onClick={() => onSelect(index)}
                                sx={rowSx}
                            >
                                {content}
                            </ButtonBase>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
